/**
 * Set sort hook - manages sort state for set details grid/summary views
 *
 * Similar to useCollectionSort but adapted for MinimalCard data.
 * Table view uses TanStack Table's built-in sorting, so this hook
 * is primarily for grid and summary views.
 */
import { atom, useAtom } from 'jotai';

import { getColorSortValue, parseCollectorNumber, RARITY_ORDER } from '@/lib/sortConstants';
import type { MinimalCard } from '@/services/cardDatabase';

export type SetSortField = 'name' | 'collector_number' | 'type' | 'cmc' | 'rarity' | 'colors' | 'owned';
export type SortDirection = 'asc' | 'desc';

export interface SetSort {
  field: SetSortField;
  direction: SortDirection;
}

export const SET_SORT_FIELD_LABELS: Record<SetSortField, string> = {
  name: 'Name',
  collector_number: 'Collector #',
  type: 'Type',
  cmc: 'Mana Value',
  rarity: 'Rarity',
  colors: 'Colors',
  owned: 'Owned',
};

const defaultSort: SetSort = {
  field: 'collector_number',
  direction: 'asc',
};

const setSortAtom = atom<SetSort>(defaultSort);

export interface UseSetSortReturn {
  sort: SetSort;
  setSort: (sort: SetSort) => void;
  setField: (field: SetSortField) => void;
  toggleDirection: () => void;
  resetSort: () => void;
}

export default function useSetSort(): UseSetSortReturn {
  const [sort, setSortState] = useAtom(setSortAtom);

  const setSort = (newSort: SetSort) => {
    setSortState(newSort);
  };

  const setField = (field: SetSortField) => {
    setSortState((prev) => ({
      field,
      // Reset to asc when changing field, toggle if same field
      direction: prev.field === field ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc',
    }));
  };

  const toggleDirection = () => {
    setSortState((prev) => ({
      ...prev,
      direction: prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const resetSort = () => {
    setSortState(defaultSort);
  };

  return {
    sort,
    setSort,
    setField,
    toggleDirection,
    resetSort,
  };
}

/**
 * Create a sort comparator for MinimalCard with ownership data
 *
 * @param sort - Current sort configuration
 * @param ownedQuantities - Map of scryfall_id -> owned quantity
 */
export function createSetSortComparator(sort: SetSort, ownedQuantities: Map<string, number>): (a: MinimalCard, b: MinimalCard) => number {
  return (a: MinimalCard, b: MinimalCard): number => {
    let comparison = 0;

    switch (sort.field) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;

      case 'collector_number': {
        const cnA = parseCollectorNumber(a.collector_number);
        const cnB = parseCollectorNumber(b.collector_number);
        comparison = cnA.num - cnB.num;
        if (comparison === 0) {
          comparison = cnA.suffix.localeCompare(cnB.suffix);
        }
        break;
      }

      case 'type':
        comparison = (a.type_line ?? '').localeCompare(b.type_line ?? '');
        break;

      case 'cmc':
        comparison = (a.cmc ?? 0) - (b.cmc ?? 0);
        break;

      case 'rarity':
        comparison = (RARITY_ORDER[a.rarity ?? 'common'] ?? 0) - (RARITY_ORDER[b.rarity ?? 'common'] ?? 0);
        break;

      case 'colors':
        comparison = getColorSortValue(a.color_identity) - getColorSortValue(b.color_identity);
        break;

      case 'owned': {
        // Owned cards first, then by quantity descending
        const aQty = ownedQuantities.get(a.id) ?? 0;
        const bQty = ownedQuantities.get(b.id) ?? 0;
        const aOwned = aQty > 0 ? 1 : 0;
        const bOwned = bQty > 0 ? 1 : 0;

        // Primary: owned status (owned first in asc mode)
        comparison = bOwned - aOwned;

        // Secondary: quantity (higher quantity first)
        if (comparison === 0) {
          comparison = bQty - aQty;
        }

        // Tertiary: collector number for stable ordering
        if (comparison === 0) {
          const cnA = parseCollectorNumber(a.collector_number);
          const cnB = parseCollectorNumber(b.collector_number);
          comparison = cnA.num - cnB.num;
          if (comparison === 0) {
            comparison = cnA.suffix.localeCompare(cnB.suffix);
          }
        }
        break;
      }
    }

    return sort.direction === 'asc' ? comparison : -comparison;
  };
}
